import { redirect } from '@tanstack/react-router';
import type { PermissionAction, PermissionSubject, UserType } from '@bliks-io/contracts';
import type { V2RouterContext } from '../../routes/__root';
import { profileQueryOptions } from '@/api/queries/useProfile';
import { requireAuthenticated } from './authSnapshot';

export type { UserType };

interface PermissionGuardOptions {
  requiredPermissions: UserType[];
  redirectTo?: string;
  requireAll?: boolean; // If true, user must have ALL permissions (AND logic)
}

// Auth gate first, THEN the profile fetch — order matters: never hit the
// profile endpoint tokenless.
async function requireAuthenticatedProfile(context: Pick<V2RouterContext, 'queryClient'>) {
  const auth = requireAuthenticated();
  // revalidateIfStale: still resolves instantly from cache, but kicks off a
  // background refetch past staleTime — bounds how long a revoked permission
  // keeps authorizing (focus-refetch is globally disabled).
  const profile = await context.queryClient.ensureQueryData({
    ...profileQueryOptions,
    revalidateIfStale: true
  });
  return { auth, profile };
}

/**
 * Factory function to create permission guards for routes
 * Checks if user has required permissions and redirects if unauthorized
 *
 * @param options - Configuration for permission requirements
 * @returns beforeLoad function for TanStack Router
 *
 * @example
 * // Single permission required (OR logic by default)
 * createPermissionGuard({ requiredPermissions: ['org_manager'] })
 *
 * @example
 * // Multiple permissions with OR logic (user needs at least one)
 * createPermissionGuard({ requiredPermissions: ['org_manager', 'system_admin'] })
 *
 * @example
 * // Multiple permissions with AND logic (user needs all)
 * createPermissionGuard({
 *   requiredPermissions: ['org_manager', 'system_admin'],
 *   requireAll: true
 * })
 */
export function createPermissionGuard(options: PermissionGuardOptions) {
  const { requiredPermissions, redirectTo = '/$slug', requireAll = false } = options;

  return async ({ context }: RouteGuardArgs) => {
    const { auth, profile } = await requireAuthenticatedProfile(context);
    const userTypes = profile.data.userTypes || [];

    const hasPermission = requireAll
      ? requiredPermissions.every((perm) => userTypes.includes(perm))
      : requiredPermissions.some((perm) => userTypes.includes(perm));

    if (!hasPermission) {
      throw redirect({
        to: redirectTo,
        params: { slug: auth.slug }
      });
    }

    return { userPermissions: userTypes };
  };
}

/**
 * Guard that requires org_manager permission
 * Redirects to organization home if user lacks permission
 */
export const createOrgManagerGuard = () =>
  createPermissionGuard({ requiredPermissions: ['org_manager'] });

type RouteGuardArgs = {
  context: Pick<V2RouterContext, 'queryClient'>;
  params: Record<string, string>;
};

/**
 * CASL-based route guard that checks the profile's permission matrix.
 * Replaces role-based guards with fine-grained subject/action checks.
 */
export function createCaslGuard(subject: PermissionSubject, action: PermissionAction = 'read') {
  return async ({ context }: RouteGuardArgs) => {
    const { auth, profile } = await requireAuthenticatedProfile(context);
    const perms = profile.permissions?.[subject];

    if (perms && perms[action]) {
      return {};
    }

    throw redirect({ to: '/$slug', params: { slug: auth.slug } });
  };
}

interface FallbackRedirect {
  subject: PermissionSubject;
  /** The action the fallback's own guard checks; the primary's by default. */
  action?: PermissionAction;
  to: string;
}

/**
 * Route guard that checks a primary permission and, on failure,
 * redirects to the first fallback the user has access to.
 * Falls through silently if no fallback matches (renders component).
 */
export function createCaslRedirectGuard(
  primary: PermissionSubject,
  fallbacks: FallbackRedirect[],
  action: PermissionAction = 'read'
) {
  return async ({ context, params }: RouteGuardArgs) => {
    const { profile } = await requireAuthenticatedProfile(context);

    if (profile.permissions?.[primary]?.[action]) return {};

    for (const fb of fallbacks) {
      if (profile.permissions?.[fb.subject]?.[fb.action ?? action]) {
        throw redirect({
          to: fb.to,
          params: { slug: params.slug }
        });
      }
    }
  };
}
